import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  Avatar,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';
import './ReferralsPage.css';

// ============================================================================
// REFERRALS PAGE - Shows user's referral link and referred users
// ============================================================================

interface Referral {
  odid: string;
  username: string;
  photoURL?: string;
  signedUpAt: Date;
}

const ReferralsPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralCount, setReferralCount] = useState(0);

  // Load referrals
  useEffect(() => {
    const loadReferrals = async () => {
      if (!currentUser) return;

      try {
        const data = await userService.getUserReferrals(currentUser.uid);
        setReferrals(data.referrals);
        setReferralCount(data.count);
      } catch (error) {
        console.error('Error loading referrals:', error);
      } finally {
        setLoading(false);
      }
    };

    loadReferrals();
  }, [currentUser]);

  // Copy referral link
  const copyReferralLink = () => {
    const referralUrl = `https://pokersweepstars.web.app/login?ref=${userProfile?.referralCode || ''}`;
    navigator.clipboard.writeText(referralUrl);
    toast({
      title: 'Copied!',
      description: 'Referral link copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  // Copy just the code
  const copyReferralCode = () => {
    navigator.clipboard.writeText(userProfile?.referralCode || '');
    toast({
      title: 'Copied!',
      description: 'Referral code copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  // Get initials for avatar
  const getInitials = (username: string) => {
    return username.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className="referrals-page">
        <div className="referrals-loading">
          <Spinner size="xl" color="cyan.400" />
          <Text color="gray.400" mt={4}>Loading referrals...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="referrals-page">
      <div className="referrals-container">
        {/* Header */}
        <div className="referrals-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1> Your Referrals</h1>

          <p>Share your link and earn rewards when friends sign up!</p>
        </div>

        {/* Referral Link Card */}
        <div className="referral-link-card">
          <div className="referral-link-header">
            <span className="referral-icon"></span>

            <Text fontWeight="bold" color="white" fontSize="lg">Your Referral Link</Text>
          </div>

          <div className="referral-link-box">
            <Text 
              color="cyan.400" 
              fontSize="sm" 
              fontFamily="mono"
              wordBreak="break-all"
            >
              https://pokersweepstars.web.app/login?ref={userProfile?.referralCode}
            </Text>
          </div>

          <div className="referral-buttons">
            <button className="referral-btn primary" onClick={copyReferralLink}>
               Copy Link

            </button>
            <button className="referral-btn secondary" onClick={copyReferralCode}>
              Copy Code Only
            </button>
          </div>

          <div className="referral-bonus-info">
            <Text color="green.400" fontSize="sm" fontWeight="semibold">
               You BOTH get $5 PSS Coins when friends sign up!

            </Text>
          </div>
        </div>

        {/* Stats Card */}
        <div className="referral-stats-card">
          <div className="stat-item">
            <span className="stat-number">{referralCount}</span>
            <span className="stat-label">Total Referrals</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-number">{userProfile?.referralCode || '---'}</span>
            <span className="stat-label">Your Code</span>
          </div>
        </div>

        {/* Referrals List */}
        <div className="referrals-list-card">
          <div className="referrals-list-header">
            <Text fontWeight="bold" color="white" fontSize="md">
              Referred Friends ({referralCount})
            </Text>
          </div>

          {referrals.length === 0 ? (
            <div className="no-referrals">
              <span className="no-referrals-icon"></span>

              <Text color="gray.400" fontSize="md" mb={2}>
                No referrals yet
              </Text>
              <Text color="gray.600" fontSize="sm">
                Share your referral link with friends to see them here!
              </Text>
            </div>
          ) : (
            <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.700" />}>
              {referrals.map((referral) => (
                <HStack
                  key={referral.odid}
                  p={4}
                  _hover={{ bg: 'rgba(255,255,255,0.03)' }}
                  cursor="pointer"
                  onClick={() => navigate(`/profile/${referral.odid}`)}
                >
                  <Avatar
                    size="md"
                    name={referral.username}
                    src={referral.photoURL}
                    bg="cyan.600"
                  >
                    {!referral.photoURL && (
                      <Text color="white" fontWeight="bold">
                        {getInitials(referral.username)}
                      </Text>
                    )}
                  </Avatar>
                  <VStack align="start" spacing={0} flex={1}>
                    <Text color="white" fontWeight="semibold" fontSize="md">
                      {referral.username}
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                      Joined {formatDate(referral.signedUpAt)}
                    </Text>
                  </VStack>
                  <Badge colorScheme="green" fontSize="xs">
                     Signed Up

                  </Badge>
                </HStack>
              ))}
            </VStack>
          )}
        </div>

        {/* Tips Section */}
        <div className="referral-tips-card">
          <Text fontWeight="bold" color="cyan.400" mb={3}>
             Tips to Get More Referrals

          </Text>
          <VStack align="start" spacing={2} fontSize="sm" color="gray.400">
            <Text>• Share your link on social media</Text>
            <Text>• Invite friends from your poker group</Text>
            <Text>• Post in poker forums and communities</Text>
            <Text>• You BOTH get $5 PSS Coins - mention it!</Text>
          </VStack>
        </div>
      </div>
    </div>
  );
};

export default ReferralsPage;